import { LitElement, html, css, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { PreviewType } from "../lib/preview.js";
import "./ibcs-preview.js";

/** Base path of the stored-snapshot route (Phase G server contract). */
export const SNAPSHOT_BASE = "/api/registry/preview";
/** A freshly published version may still be capturing; re-probe a few times. */
export const MAX_RETRIES = 3;
export const RETRY_MS = 3000;

/**
 * snapshotUrl builds GET /api/registry/preview/{scope}/{name}/{version}. The
 * query is a cache key only: the same path once served HTML snapshots with a
 * one-year immutable Cache-Control.
 */
export function snapshotUrl(scope: string, name: string, version: string): string {
  return `${SNAPSHOT_BASE}/${encodeURIComponent(scope)}/${encodeURIComponent(name)}/${encodeURIComponent(version)}?format=png`;
}

/**
 * snapshot-preview shows the stored per-version preview snapshot, falling back
 * to the schematic <ibcs-preview> when none exists. It probes the snapshot URL
 * with default caching (the 200 carries max-age + immutable, so the browser
 * caches it without revalidation; force-cache must NOT be used — 404s are
 * heuristically cacheable and it would pin the pending-window preview_not_ready
 * forever): 200 shows the PNG as an <img>; a 404 whose body code is
 * `preview_not_ready` (capture still pending right after publish) re-probes up
 * to MAX_RETRIES times RETRY_MS apart before falling back; any other failure
 * (preview_not_found, 410, network) falls back immediately. Self-contained —
 * emits nothing.
 */
@customElement("snapshot-preview")
export class SnapshotPreview extends LitElement {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }
    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: contain;
      background: #fff;
    }
    ibcs-preview {
      width: 100%;
      height: 100%;
    }
  `;

  @property() scope = "";
  @property() name = "";
  @property() version = "";
  @property({ attribute: false }) fallbackType: PreviewType = "column";
  /** Delay between preview_not_ready re-probes (test knob). */
  @property({ type: Number }) retryDelayMs = RETRY_MS;

  @state() private phase: "probing" | "ready" | "fallback" = "probing";
  @state() private src = "";

  /** Bumped per restart so stale probe responses/timers are ignored. */
  private epoch = 0;
  private retryTimer?: ReturnType<typeof setTimeout>;

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.epoch++;
    clearTimeout(this.retryTimer);
  }

  override updated(changed: PropertyValues) {
    if (changed.has("scope") || changed.has("name") || changed.has("version")) {
      this.restart();
    }
  }

  private restart() {
    clearTimeout(this.retryTimer);
    const epoch = ++this.epoch;
    this.src = "";
    if (!this.scope || !this.name || !this.version) {
      this.phase = "fallback";
      return;
    }
    this.phase = "probing";
    void this.probe(snapshotUrl(this.scope, this.name, this.version), 0, epoch);
  }

  private async probe(url: string, attempt: number, epoch: number): Promise<void> {
    try {
      const res = await fetch(url);
      if (epoch !== this.epoch) return;
      if (res.ok) {
        this.src = url;
        this.phase = "ready";
        return;
      }
      let code = "";
      try {
        code = ((await res.json()) as { code?: string } | null)?.code ?? "";
      } catch {
        /* non-JSON error body */
      }
      if (epoch !== this.epoch) return;
      if (res.status === 404 && code === "preview_not_ready" && attempt < MAX_RETRIES) {
        this.retryTimer = setTimeout(() => void this.probe(url, attempt + 1, epoch), this.retryDelayMs);
        return;
      }
      this.phase = "fallback";
    } catch {
      if (epoch !== this.epoch) return;
      this.phase = "fallback";
    }
  }

  override render() {
    if (this.phase === "ready") {
      return html`<img src=${this.src} alt="Preview of ${this.name}" />`;
    }
    if (this.phase === "fallback") {
      return html`<ibcs-preview type=${this.fallbackType}></ibcs-preview>`;
    }
    return html``;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "snapshot-preview": SnapshotPreview;
  }
}
